import { apiClient } from './api/apiClient';
import { API_ENDPOINTS } from '../config';
import {
  LandingPageGlobal,
  HeaderConfig,
  FooterConfig,
  HeadingsGlobal,
  SocialToolboxConfig,
  ApiResponse,
} from '../types';

/**
 * CMS Global Content Service
 * Handles fetching and managing global CMS configurations
 */
export class CMSService {
  /**
   * Fetch landing page configuration
   */
  static async getLandingPageConfig(): Promise<ApiResponse<LandingPageGlobal>> {
    const raw = await apiClient.get<{ key: string; config: Record<string, any>; isValid: boolean; updatedAt?: string }>(API_ENDPOINTS.GLOBAL_LANDING_PAGE, false);
    if (raw.statusCode !== 200 || !raw.data?.config) {
      return { statusCode: raw.statusCode, data: null, error: raw.error };
    }
    // API returns keys as PascalCase_With_Underscores (confirmed from live response)
    const c = raw.data.config;
    const vs = c.Video_Section ?? {};
    const wcu = c.Why_Choose_Us ?? {};
    const ts = c.Test_Sets ?? {};
    const ls = c.Learning_Space_Section ?? {};
    const sh = c.Student_Honors_Section ?? {};
    const las = c.Latest_Announcement_Section ?? {};

    const mapped: LandingPageGlobal = {
      _id: raw.data.key,
      videoSection: {
        title: vs.title ?? '',
        subtitle: vs.subtitle ?? '',
        backgroundImage: vs.background_image ?? '',
        videoUrl: vs.background_image ?? '',
        ctaText: vs.cta_text ?? '',
        ctaLink: vs.cta_link ?? '',
        hashtags: vs.hashtags ?? [],
        slogans: (vs.slogan ?? []).map((s: any, i: number) => ({
          id: s.id ?? String(i + 1),
          title: s.title ?? '',
          subtitle: s.subtitle ?? '',
          description: s.description ?? '',
        })),
      },
      whyChooseUs: {
        title: wcu.section_title ?? '',
        titlePrefix: wcu.title_prefix ?? '',
        brandName: wcu.brand_name ?? '',
        brandColor: wcu.brand_color ?? '',
        cardBg: wcu.card_bg ?? '',
        features: (wcu.features ?? []).map((f: any) => ({
          icon: f.icon ?? '',
          title: f.title ?? '',
          description: f.description ?? '',
        })),
        stats: (wcu.stats ?? []).map((s: any) => ({
          value: s.value ?? '',
          label: s.label ?? '',
        })),
      },
      testSets: {
        title: ts.section_title ?? '',
        description: ts.description ?? '',
      },
      learningSpaceSection: {
        title: ls.section_title ?? '',
        description: ls.description ?? '',
        images: ls.images ?? [],
        features: (ls.features ?? []).map((f: any) => ({
          icon: f.icon ?? '',
          text: f.text ?? '',
        })),
      },
      studentHonorsSection: sh,
      latestAnnouncementSection: {
        title: las.section_title ?? '',
        description: las.description ?? '',
        posts: las.posts ?? [],
      },
      updatedAt: raw.data.updatedAt ? new Date(raw.data.updatedAt) : new Date(),
      updatedBy: 'cms',
    } as LandingPageGlobal;
    return { statusCode: 200, data: mapped, error: null };
  }

  /**
   * Fetch header configuration
   */
  static async getHeaderConfig(): Promise<ApiResponse<HeaderConfig>> {
    const raw = await apiClient.get<{ key: string; config: Record<string, any>; isValid: boolean; updatedAt?: string }>(API_ENDPOINTS.GLOBAL_HEADER, false);
    if (raw.statusCode !== 200 || !raw.data?.config) {
      return { statusCode: raw.statusCode, data: null, error: raw.error };
    }
    const c = raw.data.config;
    const mapped: HeaderConfig = {
      _id: raw.data.key,
      logo: c.logo ?? { url: '', linkTo: '/' },
      navigation: c.navigation ?? [],
      ctaButtons: c.ctaButtons,
      updatedAt: raw.data.updatedAt ? new Date(raw.data.updatedAt) : new Date(),
    };
    return { statusCode: 200, data: mapped, error: null };
  }

  /**
   * Fetch footer configuration
   */
  static async getFooterConfig(): Promise<ApiResponse<FooterConfig>> {
    const raw = await apiClient.get<{ key: string; config: Record<string, any>; isValid: boolean; updatedAt?: string }>(API_ENDPOINTS.GLOBAL_FOOTER, false);
    if (raw.statusCode !== 200 || !raw.data?.config) {
      return { statusCode: raw.statusCode, data: null, error: raw.error };
    }
    const c = raw.data.config;
    const mapped: FooterConfig = {
      _id: raw.data.key,
      company: c.company ?? { name: '', description: '', logo: '' },
      sections: c.sections ?? [],
      socialLinks: c.social_links ?? [],
      contact: c.contact ?? { email: '', phone: '', address: '' },
      copyright: c.copyright ?? '',
      updatedAt: raw.data.updatedAt ? new Date(raw.data.updatedAt) : new Date(),
    };
    return { statusCode: 200, data: mapped, error: null };
  }

  /**
   * Fetch social toolbox configuration
   */
  static async getSocialToolboxConfig(): Promise<ApiResponse<SocialToolboxConfig>> {
    const raw = await apiClient.get<{ key: string; config: Record<string, any>; isValid: boolean }>(API_ENDPOINTS.GLOBAL_SOCIAL_TOOLBOX, false);
    if (raw.statusCode !== 200 || !raw.data?.config) {
      return { statusCode: raw.statusCode, data: null, error: raw.error };
    }
    const c = raw.data.config;
    return {
      statusCode: 200,
      data: { _id: raw.data.key, links: c.links ?? [] },
      error: null,
    };
  }

  /**
   * Fetch headings configuration
   */
  static async getHeadingsConfig(): Promise<ApiResponse<HeadingsGlobal>> {
    return apiClient.get<HeadingsGlobal>(API_ENDPOINTS.GLOBAL_HEADINGS, false);
  }

  /**
   * Update landing page configuration (admin only)
   */
  static async updateLandingPageConfig(
    data: Partial<LandingPageGlobal>
  ): Promise<ApiResponse<LandingPageGlobal>> {
    return apiClient.patch<LandingPageGlobal>(
      API_ENDPOINTS.GLOBAL_LANDING_PAGE,
      data,
      true
    );
  }

  /**
   * Update header configuration (admin only)
   */
  static async updateHeaderConfig(
    data: Partial<HeaderConfig>
  ): Promise<ApiResponse<HeaderConfig>> {
    return apiClient.patch<HeaderConfig>(
      API_ENDPOINTS.GLOBAL_HEADER,
      data,
      true
    );
  }

  /**
   * Update footer configuration (admin only)
   */
  static async updateFooterConfig(
    data: Partial<FooterConfig>
  ): Promise<ApiResponse<FooterConfig>> {
    return apiClient.patch<FooterConfig>(
      API_ENDPOINTS.GLOBAL_FOOTER,
      data,
      true
    );
  }
}

export default CMSService;
